import { motion } from "framer-motion";
import { Clock, Heart, ShieldCheck, Users } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionTag from "@/components/SectionTag";
import CtaBand from "@/components/CtaBand";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Military Family-Owned & Operated",
    text: "Service is in our DNA. The same dedication and integrity that define military life shape how we care for every patient.",
  },
  {
    icon: Heart,
    title: "Relationships, Not Transactions",
    text: "We build long-term patient relationships—taking time to listen, to know you, and to care for you as a person.",
  },
  {
    icon: Clock,
    title: "Evening & Weekend Access",
    text: "Appointments Wednesday through Sunday, 8:00 AM–8:00 PM, because care should fit your life—not the other way around.",
  },
  {
    icon: Users,
    title: "A Family-Friendly Clinic",
    text: "A warm, welcoming setting for kids and adults alike—from the waiting room to the exam room.",
  },
];

const GALLERY = [
  {
    src: "/images/waiting-room.jpg",
    alt: "The waiting room at Advantage Primary Health with a kids' corner",
    caption: "The waiting room — kids welcome",
    testid: "gallery-waiting-room",
  },
  {
    src: "/images/pediatric-room.jpg",
    alt: "A pediatric exam room with games and colorful art",
    caption: "A pediatric exam room built for smiles",
    testid: "gallery-pediatric-room",
  },
  {
    src: "/images/exam-room.jpg",
    alt: "A modern exam room at Advantage Primary Health",
    caption: "A calm, modern exam room",
    testid: "gallery-exam-room",
  },
  {
    src: "/images/care-desk.jpg",
    alt: "The care coordination desk at Advantage Primary Health",
    caption: "Where your care is coordinated",
    testid: "gallery-care-desk",
  },
  {
    src: "/images/clinic-interior.jpg",
    alt: "The welcome area inside Advantage Primary Health",
    caption: "A warm welcome, every visit",
    testid: "gallery-clinic-interior",
  },
  {
    src: "/images/jeremy-exam-wide.webp",
    alt: "Jeremy Pene, FNP-C examining a patient in an exam room",
    caption: "Real visits, real relationships",
    testid: "gallery-jeremy-exam",
  },
  {
    src: "/images/jeremy-consult.webp",
    alt: "Jeremy Pene, FNP-C in conversation with a patient",
    caption: "Time to actually listen",
    testid: "gallery-jeremy-consult",
  },
  {
    src: "/images/jeremy-exam-portrait.webp",
    alt: "Jeremy Pene, FNP-C listening to a patient's heartbeat",
    caption: "Care, up close",
    testid: "gallery-jeremy-portrait",
  },
  {
    src: "/images/ribbon-cutting.jpg",
    alt: "Advantage Primary Health ribbon cutting with the Prescott Valley community",
    caption: "Our ribbon cutting with the community",
    testid: "gallery-ribbon-cutting",
  },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <Seo
        title="About Advantage Primary Health | Military Family-Owned Clinic in Prescott Valley, AZ"
        description="Advantage Primary Health is a military family-owned primary care practice in Prescott Valley, AZ, built around better care for our community."
      />

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-14 sm:px-6 md:pt-20 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <SectionTag number="01" label="Our Story" />
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">
              Built Around{" "}
              <span className="font-serif italic font-medium text-brand-red">
                Better Care
              </span>{" "}
              for Our Community
            </h1>
            <p className="mt-7 text-base leading-relaxed text-slate-600 sm:text-lg">
              Advantage Primary Health began with a simple belief: the people
              of Prescott Valley deserve primary care that feels personal. As a
              military family-owned and operated practice, we built our clinic
              the way we would want care for our own family—unhurried
              appointments, a provider who truly listens, and hours that
              respect real life.
            </p>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              From our ribbon cutting with the community to every appointment
              since, our promise has stayed the same: compassionate care,
              strong patient relationships, and access when traditional clinics
              are often closed. We are now accepting new patients, and we would
              be honored to care for your family.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-slate-50/70">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-24 lg:px-12">
          <Reveal>
            <SectionTag number="02" label="What We Stand For" />
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div
                  data-testid={`pillar-card-${i + 1}`}
                  className="group h-full rounded-3xl border border-slate-100 bg-white p-8 shadow-soft transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lift"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-redlight transition-colors duration-300 group-hover:bg-brand-red">
                    <p.icon className="h-5 w-5 text-brand-red transition-colors duration-300 group-hover:text-white" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-brand-navy">
                    {p.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-500">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-24 lg:px-12">
          <Reveal>
            <SectionTag number="03" label="Inside the Clinic" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl">
              A warm welcome,{" "}
              <span className="font-serif italic text-brand-red">every visit</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {GALLERY.map((photo, i) => (
              <Reveal key={photo.src} delay={(i % 3) * 0.08}>
                <figure data-testid={photo.testid}>
                  <div className="group overflow-hidden rounded-[2rem] shadow-soft">
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <figcaption className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                    {photo.caption}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="Come experience the difference."
        accent="the difference."
        sub="We are now accepting new patients. Book online or call for same-day care."
        testidPrefix="about-cta"
      />
    </main>
  );
}
